import React from 'react';
import type { StyleProp, ViewStyle } from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useDerivedValue,
  withTiming,
} from 'react-native-reanimated';

import { Stack } from '@/components/layout';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';

/** How long a segment takes to fill when the step advances. */
const FILL_DURATION_MS = 300;

type OnboardingStepperProps = {
  /** Zero-based index of the active step. */
  currentStep: number;
  /** Total number of steps to render as segments. */
  totalSteps: number;
};

type StepSegmentProps = {
  /** Whether this segment should be filled at the current step. */
  filled: boolean;
  trackStyle: StyleProp<ViewStyle>;
  fillStyle: StyleProp<ViewStyle>;
};

/**
 * A single stepper segment. Its fill width animates linearly between empty and
 * full whenever `filled` flips, so advancing a step slides the bar across
 * instead of snapping.
 */
function StepSegment({ filled, trackStyle, fillStyle }: StepSegmentProps) {
  const progress = useDerivedValue(() =>
    withTiming(filled ? 1 : 0, { duration: FILL_DURATION_MS, easing: Easing.linear }),
  );

  const animatedFillStyle = useAnimatedStyle(() => ({
    width: `${progress.value * 100}%`,
  }));

  return (
    <Stack flex={1} style={trackStyle}>
      <Animated.View style={[fillStyle, animatedFillStyle]} />
    </Stack>
  );
}

/**
 * Segmented progress bar shown at the top of the onboarding wizard. Each step is a
 * rounded track: completed steps fill fully, the active step fills halfway (the
 * "in progress" look from Figma), upcoming steps stay empty. Fills animate
 * linearly as the user moves between steps.
 */
export function OnboardingStepper({ currentStep, totalSteps }: OnboardingStepperProps) {
  const { scale } = useScaling();
  const styles = useStyles();

  return (
    <Stack direction="row" align="center" gap={scale(2)} style={styles.container}>
      {Array.from({ length: totalSteps }).map((_, index) => (
        <StepSegment
          key={index}
          filled={index <= currentStep}
          trackStyle={styles.track}
          fillStyle={styles.fill}
        />
      ))}
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  container: {
    width: scale(206),
  },
  track: {
    height: scale(10),
    borderRadius: scale(30),
    backgroundColor: colors['0'],
    shadowColor: colors[10],
    shadowOffset: { width: 0, height: scale(2) },
    shadowOpacity: 0.3,
    shadowRadius: scale(8),
    elevation: 4,
  },
  fill: {
    height: '100%',
    borderRadius: scale(10),
    backgroundColor: guardianColors['Main/Main'],
  },
}));

export default OnboardingStepper;
